import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root — full-width white section with rounded bottom                  */
/* ------------------------------------------------------------------ */
export const StyledRoot = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: ${colors.light.bg.primary};
  border-radius: 0 0 ${sizes.borderRadius[24]}px ${sizes.borderRadius[24]}px;

  @media (min-width: ${breakpoints.lg}px) {
    border-radius: 0 0 ${sizes.borderRadius[40]}px ${sizes.borderRadius[40]}px;
  }
`

/* Street-map background. A white gradient fades it out toward the form so text
   stays legible. EGDE-215: on mobile it sits as a band behind the decorative at
   the top (fading down); on desktop it anchors to the inline-start edge (right
   in RTL) and fades toward the form. (The mobile band regressed when an older
   FormShell.styles revision was reapplied in EGDE-223 — restored here.) */
export const StyledMapBg = styled.div`
  display: block;
  position: absolute;
  top: 0;
  inset-inline: 0;
  height: 300px;
  z-index: 0;
  background-image: url("/images/contact-us/map-bg.png");
  background-size: cover;
  background-position: top center;

  /* EGDE-292: fade the map down to white. Colour stops are IMPLICIT — the prod
     build's stylis-plugin-rtl flips explicit 0%/100% stops on RTL pages, which
     collapsed both stops to 100% (no fade) on the live Hebrew form. Leaving the
     stops implicit lets the gradient render correctly in both directions, same
     as StyledDecorative::before (EGDE-262). */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    );
  }

  /* EGDE-227: on desktop the street map is no longer a full-section band. It is
     confined to the fixed 800×800 "Image + bg" box rendered by StyledDecorative
     (matching the Figma frames), so this mobile-only band is hidden here. */
  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* ------------------------------------------------------------------ */
/* Content wrapper — form (left) + decorative X (right) on desktop      */
/* ------------------------------------------------------------------ */
export const StyledContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${sizes.spacing[24]}px;
  /* EGDE-332: no bottom padding — the footer supplies its own 64px top padding,
     so a bottom pad here collided with it into a 128px form→footer gap. */
  padding: ${sizes.spacing[48]}px ${sizes.spacing[16]}px 0;

  /* EGDE-214: padding is ASYMMETRIC. The decorative/image side (inline-start =
     the right edge in RTL) keeps a small fixed inset so the X-logo + map box hug
     that edge; the form-content side (inline-end) follows the design's ladder
     (80px @1280, 120px @1440, 360px @1920). The previous symmetric max() clamp
     applied the full 360px to BOTH sides at 1920, which QA rejected — the image
     side must be < 360 so the image "aligns more to the right". */
  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    /* EGDE-339: top-align the form against the fixed 800px decorative box.
       Centering pushed the short form down, leaving a large empty band above
       the title (QA: "too much gap" vs Figma's near-flush form). */
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0;
    /* EGDE-332: bottom 0 (see base) so only the footer's 64px is the gap. */
    padding-block: ${sizes.spacing[64]}px 0;
    /* Image side (inline-start) hugs the section edge; only the form side keeps
       the design's horizontal-padding ladder. */
    padding-inline-start: 0;
    padding-inline-end: ${layoutPadding.desktop}px;
  }

  @media (min-width: ${breakpoints.xl}px) {
    padding-inline-end: ${layoutPadding.desktopL}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    padding-inline-end: ${layoutPadding.desktopXl}px;
  }
`

/* Decorative graphic.
   - Mobile (EGDE-215): the brand "X" logo band that stacks at the top of the
     form over the street-map band.
   - Desktop (EGDE-227): the Figma "Image + bg" box — a fixed 800×800 square
     pinned to the inline-start (RTL: right) edge holding the street-map
     background, a 375px white mask fading it toward the form, and the 393×393
     brand logo on top. QA twice rejected the earlier full-section-height map as
     "bigger than Figma"; the design confines the map to this 800px box (asset
     max 952). The box is the tallest item in the row, so it sets the section to
     800px tall with the form vertically centred — exactly like the design. */
export const StyledDecorative = styled.div`
  display: block;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 220px;
  background-image: url("/images/contact-us/x-graphic.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  /* EGDE-318: scroll-scrubbed entrance (--scrub 0→1 set by FormShell). The
     brand X fades in while growing ~2/3 → full size. On mobile the whole band
     IS the X, so the band itself scales; on desktop the X is rendered by the
     StyledXArm children — the map box must stay put, so only the arms animate.
     --scrub defaults to 1 (fully formed) so the X never disappears without JS. */
  opacity: var(--scrub, 1);
  transform: scale(calc(0.67 + 0.33 * var(--scrub, 1)));

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    flex-shrink: 0;
    width: 800px;
    /* EGDE-656: the 800px square used to be a hard height, so a form column
       shorter than the box (every form but the longest) left the section
       standing 200-450px of empty white below the card — QA: "there's a huge
       gap below the forms". The Figma 800px is a CEILING, not a floor: the box
       now stretches to whatever the form column needs and only stops growing
       at 800 (952 @1920, see below), so a short form ends 64px under the card
       while a tall one still gets the full design-size box. The stretched
       item's hypothetical cross size is 0 (its X arms are absolutely
       positioned), so the row height is the form column's alone. */
    align-self: stretch;
    height: auto;
    max-height: 800px;
    overflow: hidden;
    /* EGDE-318: desktop — the map box itself must not fade or scale (only the
       brand logo forms); undo the mobile-scoped scrub above. */
    opacity: 1;
    transform: none;
    /* EGDE-227: the "mask layer on top of the image" (Raz's spec). A uniform
       white veil over the WHOLE map box softens the street map to the faint
       level shown in Figma — the previous edge-only ::before fade left the map
       far too prominent on the live build ("background bigger than figma" /
       "mask not implemented"). The veil is a constant-alpha gradient so the
       RTL prod build's stylis-plugin-rtl has no stops/direction to flip. The
       ::before below still fades the inline-end edge fully into the form. */
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.62),
        rgba(255, 255, 255, 0.62)
      ),
      url("/images/contact-us/map-bg.png");
    background-size: cover;
    background-position: center;

    /* White mask fading the map into the form on the inline-end edge. Colour
       stops are implicit so stylis-plugin-rtl flips only the gradient direction
       in RTL, never the stops (EGDE-262). */
    &::before {
      content: "";
      position: absolute;
      inset-block: 0;
      inset-inline-end: 0;
      width: 375px;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1)
      );
    }
  }

  /* EGDE-227: at 1920 the map box grows to the asset's max 952×952 (Figma node
     40015789-53115); 1280/1440 stay 800×800. */
  @media (min-width: ${breakpoints.xxl}px) {
    width: 952px;
    max-height: 952px;
  }
`

/* EGDE-318: the desktop brand X, split into its TWO arms (x-arm-start = the
   orange chevron, x-arm-end = the photo chevron — both on the full 800×800
   canvas so stacking them reproduces x-graphic.png exactly). In the prototype
   the arms CONVERGE: each grows from ~half size around its own centre while
   fading in, so they start apart and interlock into the X at rest. Driven by
   the same --scrub var; transform-origin is each arm's own centroid in
   element px (px origins are direction-safe — stylis-plugin-rtl cannot flip
   them, and the artwork itself is identical in RTL/LTR). Replaces the old
   single-image ::after (which could only scale the whole X). */
const xArmBase = css`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    position: absolute;
    /* EGDE-486: in Figma the logo centre and the form column centre coincide
       (both centred in the 800px row). Our form is top-aligned (EGDE-339), so
       a box-centred logo sat visibly lower than the form. FormShell measures
       the form column and publishes its centre as --form-center; the box
       centre stays as the no-JS fallback. */
    top: var(--form-center, 50%);
    inset-inline-start: 120px;
    /* Centring via margin, not translateY — transform belongs to the scrub. */
    margin-top: -196.5px;
    width: 393px;
    height: 393px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    /* EGDE-510 point 7: the spec is explicit that on DESKTOP the logo "grows
       from zero opacity to 100% opacity fade in" — i.e. a pure fade. The arms
       used to also scale from half size around their own centroids, which is
       what QA filed as "the logo grows on desktop too, it shouldn't be this
       way. Only the opacity should change." Mobile keeps its scale (see
       StyledDecorative) because there the spec does ask for the logo to grow. */
    opacity: var(--scrub, 1);

    @media (prefers-reduced-motion: reduce) {
      opacity: 1;
    }
  }

  /* EGDE-227: logo re-centred in the larger 952px box at 1920. */
  @media (min-width: ${breakpoints.xxl}px) {
    inset-inline-start: 280px;
  }
`

/* The arms no longer transform (point 7 — desktop is an opacity fade), so the
   per-arm transform-origin that used to centre their scale is gone. Kept here
   as data in case a future round restores a scale: orange-arm centroid
   (209.6, 396.3) and photo-arm centroid (588.4, 404.0) on the 800 canvas,
   × 393/800 → 103px 195px and 289px 198px. */
export const StyledXArmStart = styled.div`
  ${xArmBase}
  background-image: url("/images/contact-us/x-arm-start.png");
`

export const StyledXArmEnd = styled.div`
  ${xArmBase}
  background-image: url("/images/contact-us/x-arm-end.png");
`

/* ------------------------------------------------------------------ */
/* Form section — title + subtitle + card                               */
/* ------------------------------------------------------------------ */
export const StyledFormSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[24]}px;
  /* EGDE-450: on mobile the form column always ends the section, so without
     its own bottom padding the card sits flush against the section's clipped
     rounded bottom — especially once a form description (subtitle) pushes it
     down. The wrapper stays padding-free per EGDE-332. */
  padding-bottom: ${sizes.spacing[32]}px;

  /* Designer r2 (Raz, 2026-07-14): prototype horizontal entrance — the whole
     form column (title, subtitle, card) sweeps in from its own screen edge
     (the inline-end: left in RTL, cf. prototype frame f020 where the copy is
     clipped at that edge), driven by the same --scrub that forms the brand
     X (inherited from the content wrapper). --scrub defaults to 1 so the
     form never moves without JS; --edge supplies the LTR mirror since
     stylis-plugin-rtl does not flip transforms.

     EGDE-510 r4: --scrub is now a ONE-SHOT entrance rather than a scroll
     scrub (see useOneShotEntrance in FormShell) — the travel is identical,
     it just no longer follows the wheel or reverses on the way back up. */
  --edge: 1;

  [dir="ltr"] & {
    --edge: -1;
  }

  /* EGDE-510 point 7, mobile. The section itself does not move on mobile: the
     spec splits the beat in two — "Texts enter from right to left. Form enters
     from bottom to top." So StyledTitleRow / StyledSubtitle sweep in from the
     inline-start edge while StyledFormCard rises, each carrying its own
     transform (see their definitions below). An earlier pass moved the whole
     column vertically, which fixed "animation is missing for mobile" but merged
     the two beats the spec keeps apart. */

  @media (min-width: ${breakpoints.lg}px) {
    /* Desktop keeps the prototype's horizontal entrance and stays fully
       opaque — the sweep alone carries the beat there. */
    opacity: 1;
    flex: 1;
    max-width: 712px;
    z-index: 2;
    transform: translateX(
      calc(-1 * var(--edge) * min(22vw, 420px) * (1 - var(--scrub, 1)))
    );

    @media (prefers-reduced-motion: reduce) {
      transform: none;
    }
    /* EGDE-441: when a growing textarea makes the form column taller than the
       fixed 800px map box, the column — not the wrapper (EGDE-332) — supplies
       the breathing room, so the card never sits flush against the section's
       clipped rounded bottom. While the map box is the tallest item this
       padding is absorbed by the box height and changes nothing visually. */
    padding-bottom: ${sizes.spacing[64]}px;
    /* Overlap the decorative box's faded mask edge so the form keeps the design
       ladder while the map box stays a fixed 800px (Figma Container negative
       inline-start margin). EGDE-440: the Figma form card is a FIXED 712px at
       1440 and 1920 — the earlier -166/0 margins let flex resolve the section
       to 686px @1440 and 608px @1920. The overlap absorbs the difference so
       flex:1 caps at exactly max-width 712:
       1440 − 120(pad) − 800(map) + 192 = 712; 1920 − 360 − 952 + 104 = 712. */
    margin-inline-start: -206px;
  }

  @media (min-width: ${breakpoints.xl}px) {
    margin-inline-start: -192px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    margin-inline-start: -104px;
  }
`

/* EGDE-510 point 7 (mobile): "Texts enter from right to left." The texts carry
   their own horizontal sweep so the form card can rise independently. --edge and
   --scrub are inherited from StyledFormSection / the content wrapper. Desktop is
   excluded: there the whole column sweeps as one unit, so a second transform on
   the children would double the travel. */
const mobileTextsIn = css`
  opacity: var(--scrub, 1);
  transform: translateX(calc(var(--edge, 1) * 64px * (1 - var(--scrub, 1))));

  @media (min-width: ${breakpoints.lg}px) {
    opacity: 1;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
  }
`

export const StyledTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
  ${mobileTextsIn}
`

export const StyledTitleDecoration = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

export const StyledTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const StyledSubtitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  width: 100%;
  font-size: 20px;
  line-height: 28px;
  ${mobileTextsIn}
`

/* ------------------------------------------------------------------ */
/* Form card — white card with shadow                                   */
/* ------------------------------------------------------------------ */
export const StyledFormCard = styled.div`
  width: 100%;
  background-color: ${colors.light.bg.primary};
  border-radius: ${sizes.borderRadius[24]}px;
  box-shadow: ${colors.shadows.stripWhite};
  padding: ${sizes.spacing[24]}px;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[32]}px;
  align-items: flex-start;

  /* EGDE-510 point 7 (mobile): "Form enters from bottom to top" — the other
     half of the split beat (the texts sweep horizontally, see mobileTextsIn). */
  opacity: var(--scrub, 1);
  transform: translateY(calc(36px * (1 - var(--scrub, 1))));

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    /* Desktop moves the whole column instead (StyledFormSection). */
    opacity: 1;
    transform: none;
    border-radius: ${sizes.borderRadius[32]}px;
    padding: ${sizes.spacing[32]}px;
  }
`

export const StyledFormHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${sizes.spacing[12]}px;
`

export const StyledRequiredNote = styled.span`
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  line-height: 24px;
`

/* EGDE-528: required marks are brand orange at rest (red stays reserved for
   error states). */
export const StyledRequired = styled.span`
  color: ${colors.primitives.primary500};
`

export const StyledFieldsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
`

export const StyledFieldRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    gap: ${sizes.spacing[16]}px;
  }
`

export const StyledField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  /* EGDE-237: without this a flex column's automatic minimum size is its
     content's min-content width, so an unbreakable long file name blows the
     column past 50% and pushes the paired field out of alignment. Allowing the
     column to shrink lets the inner ellipsis (min-width:0 + overflow:hidden) do
     its job. */
  min-width: 0;
`

export const StyledFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`

export const StyledSubmitButton = styled.button`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  cursor: pointer;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  transition: box-shadow 0.2s ease;

  @media (min-width: ${breakpoints.lg}px) {
    width: 200px;
  }

  /* EGDE-500: Main CTA sheet — primary hovers to the flat orange with the
     button glow and presses to the darker orange (instant swap, EGDE-355). */
  &:hover:not(:disabled),
  &:focus-visible {
    background: ${colors.primitives.primary500};
    box-shadow: ${colors.shadows.button};
  }

  &:active:not(:disabled) {
    background: ${colors.primitives.primary600};
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const StyledSubmitText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
  white-space: nowrap;
`

/* EGDE-579: mirror MUI's FormHelperText box metrics — 14px horizontal inset
   and a matching line box — so a custom field error lines up with the messages
   MUI renders under the text inputs. EGDE-666 moved that shared box to
   14px/18px, so this mirror moves with it. */
export const StyledFieldError = styled.span`
  color: ${colors.primitives.error500};
  font-size: 14px;
  line-height: 18px;
  margin-top: 4px;
  display: block;
  padding: 0 14px;
`

/* ------------------------------------------------------------------ */
/* Success / Error states — replace the form card                       */
/* ------------------------------------------------------------------ */
export const StyledStateCard = styled.div`
  width: 100%;
  background-color: ${colors.light.bg.primary};
  border-radius: ${sizes.borderRadius[24]}px;
  box-shadow: ${colors.shadows.stripWhite};
  padding: ${sizes.spacing[48]}px ${sizes.spacing[24]}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  min-height: 400px;

  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${sizes.borderRadius[32]}px;
    padding: ${sizes.spacing[64]}px ${sizes.spacing[32]}px;
  }
`

export const StyledStateIcon = styled.div`
  /* EGDE-228: 88px to fit the larger Figma success check. */
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${sizes.spacing[8]}px;
`

export const StyledStateTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: center;
`

export const StyledStateMessage = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: center;
  max-width: 480px;
`

export const StyledRetryButton = styled.button`
  height: 52px;
  min-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  cursor: pointer;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[32]}px;
  margin-top: ${sizes.spacing[8]}px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`

export const StyledRetryText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
  white-space: nowrap;
`
